import produce from 'immer';
import { SortingRule } from 'react-table-6';
import { combineReducers } from 'redux';
import { createSelector } from 'reselect';
import { getType, PayloadAction } from 'typesafe-actions';
import { actions } from '..';
import { AgentViewTab, CallState, ContactType, EntityType, getKey, Identifier, MediaType, QueueEmail, QueueOrSquadId, QueueRegionId, QueueView, sameQueueOrSquadId } from '../../model';
import { DefaultTheme, Theme } from '../../TeamsProvider';
import { Actions, StoreState } from '../types';
import { selectSingleQueueOrSquad } from './config';

type Toggle = {
  defValue: boolean
  overrides: Record<string, boolean | undefined>
}

type ActionsOfType<T> = Extract<Actions, PayloadAction<string, T>>['type']

function reduceToggle<A1 extends ActionsOfType<boolean>, A2 extends ActionsOfType<Identifier>>(resetAction: A1, toggleAction: A2, def: boolean) {
  return function reduce(state: Toggle = { defValue: def, overrides: {} }, action: Actions): Toggle {
    switch (action.type) {
      case resetAction:
        return { defValue: action.payload, overrides: {} }
      case toggleAction: {
        const key = getKey(action.payload)
        if (state.overrides[key] === undefined || state.overrides[key] === state.defValue) {
          return { ...state, overrides: { ...state.overrides, [key]: !state.defValue } }
        } else {
          const { [key]: _, ...overrides } = state.overrides
          return { ...state, overrides }
        }
      }
    }

    return state
  }
}

function compactView(state: boolean = false, action: Actions) {
  if (action.type === getType(actions.setCompactView)) {
    return action.payload
  }

  return state
}

function queueView(state: QueueView = { region: QueueRegionId.Mine }, action: Actions) {
  if (action.type === getType(actions.setQueueView)) {
    return action.payload
  } else if (action.type === getType(actions.forceReload)) {
    return { region: QueueRegionId.Mine }
  }

  return state
}

export interface DirectoryView {
  regionId: string | 'personal'
  entityType: EntityType
  contactType?: ContactType
}

function directoryView(state: DirectoryView = { regionId: 'personal', entityType: EntityType.Extension }, action: Actions): DirectoryView {
  if (action.type === getType(actions.setDirectoryView)) {
    return action.payload
  }

  return state
}

function queueHoldKey(state: string = '', action: Actions) {
  if (action.type === getType(actions.setQueueHoldKey)) {
    if (action.payload === '') {
      state = action.payload
    } else {
      state = 'set-' + action.payload
    }
    return state
  }

  //not interested if not set
  if (state === '') { return state }

  if (action.type === getType(actions.updateMedia)) {
    const media = action.payload.media
    if (state === media.id) {
      if (media.status !== CallState.QueueHold && media.status !== CallState.WaitingForWrapup) {
        state = ''
      }
    } else if (state === "set-" + media.id) {
      // when call is put on queue hold call state will flick to ExtensionAnswered before QueueHold
      // so added "set-" to key that will be removed when call gets in to QueueHold"
      // then when state of call is not QueueHold we will remove queueHoldKey
      if (media.status === CallState.QueueHold) {
        state = media.id
      }
    }
    return state
  }

  if (action.type === getType(actions.removeMedia)) {
    if (state === action.payload.id) {
      state = ""
    }
  }

  return state
}

function splitters(state: Record<string, number> = {}, action: Actions): Record<string, number> {
  if (action.type === getType(actions.setSplitter)) {
    const { name, value } = action.payload
    return {
      ...state,
      [name]: value,
    }
  }

  return state
}

function collapsedPanels(state: Record<string, boolean> = {}, action: Actions): Record<string, boolean> {
  if (action.type === getType(actions.setPanelCollapsed)) {
    const { name, value } = action.payload
    return {
      ...state,
      [name]: value,
    }
  }

  return state
}

function expandedQueues(state: QueueOrSquadId[] = [], action: Actions): QueueOrSquadId[] {
  if (action.type === getType(actions.setExpandedQueues)) {
    return action.payload
  } else if (action.type === getType(actions.expandQueue)) {
    const queue = action.payload
    const expand = !state.some(x => sameQueueOrSquadId(x, queue))

    if (expand) {
      const id: QueueOrSquadId = { id: queue.id, server: queue.server, type: queue.type }
      return [...state, id]
    } else {
      return state.filter(x => !sameQueueOrSquadId(x, queue))
    }
  } else if (action.type === getType(actions.setQueueView)) {
    return []
  }

  return state
}

function parkedMedia(state: string[] = [], action: Actions): string[] {
  switch (action.type) {
    case getType(actions.ccQueueHold): {
      const { mediaId } = action.payload
      if (state.includes(mediaId)) {
        return state
      } else {
        return [...state, "set-" + mediaId]
      }
    }
    case getType(actions.ccHoldFor): {
      const { mediaId } = action.payload
      if (state.includes(mediaId)) {
        return state
      } else {
        return [...state, "set-" + mediaId]
      }
    }

    case getType(actions.setParkedMedia): {
      return action.payload
    }

    case getType(actions.updateMedia): {
      const media = action.payload.media
      if (state.length === 0) {
        return state
      }
      else if (state.find(x => x === media.id)) {
        if (media.status !== CallState.QueueHold && media.status !== CallState.WaitingForWrapup && media.status !== CallState.ExtensionPending) {
          return state.filter(id => id !== media.id)
        }
      } else if (state.find(x => x === "set-" + media.id)) {
        // when call is put on queue hold call state will flick to ExtensionAnswered before QueueHold
        // so added "set-" to key that will be removed when call gets in to QueueHold"
        // then when state of call is not QueueHold we will remove queueHoldKey
        if (media.status === CallState.QueueHold) {
          return [...state.filter(id => id !== "set-" + media.id), media.id]
        }
      }
      return state
    }
    /*
    case getType(actions.removeMedia): {
      return state.filter(id => id !== action.payload)
    }*/
  }

  return state
}

function outgoing(state: QueueEmail | null = null, action: Actions): QueueEmail | null {

  if (action.type === getType(actions.setNewOutgoing)) {
    return action.payload.email
  }

  if (action.type === getType(actions.removeMediaItem)) {
    if (state && state.mediaId && state.mediaId === action.payload) {
      return null
    }
  }

  return state
}

interface AgentView {
  mediaId?: string
  tab?: AgentViewTab
}

function agentView(state: AgentView = {}, action: Actions): AgentView {
  if (action.type === getType(actions.openAgentView)) {
    return action.payload
  }

  return state
}

function instantMessageView(state: string = "", action: Actions) {
  if (action.type === getType(actions.setInstantmessageView)) {
    if (state !== action.payload) {
      return action.payload
    }
  }
  return state
}
export type MediaTypesFilter = Partial<Record<MediaType, boolean>>

export type SearchFitler = {
  months: number,
  types: MediaTypesFilter
  startDate?: number,
  endDate?: number,
}

function searchFilter(state: SearchFitler = { months: 1, types: { CALL: true, VM: true, EMAIL: true, FB: true, TWEET: true, TC: true } }, action: Actions): SearchFitler {
  if (action.type === getType(actions.updateSearchTypeFilter)) {
    return {
      ...state,
      types: action.payload,
    }
  } else if (action.type === getType(actions.updateSearchMonthsFilter)) {
    return {
      ...state,
      months: action.payload,
      startDate: undefined,
      endDate: undefined,
    }
  }
  else if (action.type === getType(actions.updateSearchStartDateFilter)) {
    return {
      ...state,
      startDate: action.payload,
      months: 0,
    }
  }
  else if (action.type === getType(actions.updateSearchEndDateFilter)) {
    return {
      ...state,
      endDate: action.payload,
      months: 0,
    }
  }
  else if (action.type === getType(actions.resetSearchFilter)) {
    return { months: 1, types: action.payload.types }
  }
  return state
}



function taskFilter(state: MediaTypesFilter = { CALL: true, VM: true, EMAIL: true, FB: true, TWEET: true, TC: true }, action: Actions): MediaTypesFilter {
  if (action.type === getType(actions.updateTaskFilter)) {
    return action.payload
  }
  return state
}

export type TranscriptionFilter = {
  bookmarks: boolean,
  agent: boolean,
  caller: boolean
}

function transcriptFilter(state: TranscriptionFilter = { bookmarks: false, agent: true, caller: true }, action: Actions): TranscriptionFilter {
  if (action.type === getType(actions.updateTranscriptionFilter)) {
    return action.payload
  }
  return state
}

function inTeams(state: boolean = false, action: Actions) {
  if (action.type === getType(actions.setInTeams)) {
    return true
  }
  return state
}

function inIpfxClient(state: boolean = false, action: Actions) {
  if (action.type === getType(actions.setInIpfxElectron)) {
    return action.payload
  }
  return state
}

function inIpfxMobilityClient(state: boolean = false, action: Actions) {
  if (action.type === getType(actions.setInIpfxMobilityElectron)) {
    return action.payload
  }
  return state
}

function theme(state: Theme = DefaultTheme, action: Actions) {
  if (action.type === getType(actions.setTheme)) {
    return action.payload
  }
  return state
}

function selectedNumber(state: string = '', action: Actions) {
  if (action.type === getType(actions.setSelectedNumber)) {
    return action.payload
  }
  if (action.type === getType(actions.clearSelectedNumber)) {
    return ''
  }
  return state
}

function secondaryRingerDevice(state: { deviceId: string, displayName: string } = { deviceId: '', displayName: '' }, action: Actions) {
  if (action.type === getType(actions.setSecondaryRingerDevice)) {
    return action.payload
  }
  return state
}

export type GridId = 'queues' | 'agents' | 'extensions' | 'contacts' | 'search' | 'recent' | 'voicemail' | 'media'

type GridSort = Record<string, SortingRule[]>

function gridSorting(state: GridSort = {}, action: Actions): GridSort {
  if (action.type === getType(actions.requestSetGridSortOrder)) {
    const { grid, sort } = action.payload
    if (sort.length === 0 && state[grid] && state[grid].length === 0) { return state }
    return {
      ...state,
      [grid]: sort,
    }
  }
  return state
}

export type ColumnWidths = Record<string, number>
type GridColumnWidths = Record<string, ColumnWidths>

function gridColumnWidth(state: GridColumnWidths = {}, action: Actions): GridColumnWidths {
  if (action.type === getType(actions.requestSetGridColumnWidth)) {
    return produce(state, state => {
      const { grid, columns } = action.payload
      const data = state[grid]
      if (!data) {
        state[grid] = columns
      } else {
        for (const key in columns) {
          if (Object.prototype.hasOwnProperty.call(columns, key)) {
            data[key] = columns[key]
          }
        }
      }
    });
  }

  return state
}

function clientLogging(state: boolean = false, action: Actions) {
  if (action.type === getType(actions.setClientLogging)) {
    return action.payload
  }
  return state
}

function agentStatusTab(state: number = 0, action: Actions) {
  if (action.type === getType(actions.setAgentStatusTab)) {
    return action.payload
  }
  return state
}

function fixedQueueGridHeight(state: number = 0, action: Actions): number {
  if (action.type === getType(actions.setFixedQueueGridHeight)) {
    return action.payload
  }
  return state
}

export default combineReducers({
  compactView,
  queueView,
  queueHoldKey,
  showQueueAgents: reduceToggle(getType(actions.setShowQueueAgents), getType(actions.toggleQueueAgents), true),
  showQueueMedia: reduceToggle(getType(actions.setShowQueuingMedia), getType(actions.toggleQueuingMedia), false),
  fixedQueueGridHeight,
  splitters,
  collapsedPanels,
  expandedQueues,
  parkedMedia,
  agentView,
  instantMessageView,
  directoryView,
  outgoing,
  searchFilter,
  taskFilter,
  inTeams,
  transcriptFilter,
  theme,
  inIpfxClient,
  inIpfxMobilityClient,
  selectedNumber,
  secondaryRingerDevice,
  gridSorting,
  clientLogging,
  agentStatusTab,
  gridColumnWidth,
})

function getRoot(state: StoreState) {
  return state.localSettings
}

function getShowQueueAgents(state: StoreState) { return getRoot(state).showQueueAgents }
function getShowQueueMedia(state: StoreState) { return getRoot(state).showQueueMedia }

export function selectTheme(state: StoreState) {
  return getRoot(state).theme
}
export function selectedTableRowDialNumber(state: StoreState) {
  return getRoot(state).selectedNumber
}

export function selectInIpfxClient(state: StoreState) {
  return getRoot(state).inIpfxClient
}

export function selectInIpfxMobility(state: StoreState) {
  return getRoot(state).inIpfxMobilityClient
}
export function selectInTeams(state: StoreState) {
  return getRoot(state).inTeams
}

export function selectSearchFilters(state: StoreState) {
  return getRoot(state).searchFilter
}

export function taskSearchFilters(state: StoreState) {
  return getRoot(state).taskFilter
}

export function transcriptionSearchFilters(state: StoreState) {
  return getRoot(state).transcriptFilter
}

export function selectAgentStatusTab(state: StoreState) {
  return getRoot(state).agentStatusTab
}

function createToggleSelector(selector: (state: StoreState) => Toggle) {
  return function (state: StoreState, queueId?: Identifier) {
    const data = selector(state)
    if (queueId === undefined) {
      return Object.keys(data.overrides).length === 0 && data.defValue
    } else {
      const value = data.overrides[getKey(queueId)]
      return value === undefined ? data.defValue : value
    }
  }
}

export const selectShowQueueAgents = createToggleSelector(getShowQueueAgents)
export const selectShowQueuingMedia = createToggleSelector(getShowQueueMedia)

export function selectCompactView(state: StoreState) {
  return getRoot(state).compactView
}

export function selectSelectedQueueView(state: StoreState) {
  return getRoot(state).queueView
}

export function selectDirectoryView(state: StoreState) {
  return getRoot(state).directoryView
}

export function selectQueueHoldKey(state: StoreState): string {
  let value = getRoot(state).queueHoldKey
  return !value ? "" : value
}

function getSplitters(state: StoreState) {
  return getRoot(state).splitters
}

export function selectSplitterValue(state: StoreState, name: string): number | undefined {
  return getSplitters(state)[name]
}

function getCollapsedPanels(state: StoreState) {
  return getRoot(state).collapsedPanels
}

export function selectClientLogging(state: StoreState) {
  return getRoot(state).clientLogging
}

export function selectCollapsedPanelValue(state: StoreState, name: string): boolean | undefined {
  return getCollapsedPanels(state)[name]
}

export function getOutGoing(state: StoreState) { return getRoot(state).outgoing }

export const selectExpandedQueues = createSelector(
  selectSingleQueueOrSquad,
  (s: StoreState) => getRoot(s).expandedQueues,
  (single, expandedQueues): QueueOrSquadId[] => {
    if (single) {
      return [single]
    }
    return expandedQueues
  })

export function getParkedMedia(state: StoreState) {
  return getRoot(state).parkedMedia
}

export function selectLastAgentView(state: StoreState) {
  return getRoot(state).agentView
}

export function selectLastInstantMessageView(state: StoreState) {
  return getRoot(state).instantMessageView
}

export function isOutGoingReady(state: StoreState) {
  const email = getOutGoing(state)
  if (!email) return false
  if (!email.mediaId) return false
  if (!email.id) return false
  return true
}

export function selectSecondaryRingerDevice(state: StoreState) {
  return getRoot(state).secondaryRingerDevice
}

const emptySortOrder = [{ id: '', desc: false }]

export function getGridSortOrder(state: StoreState, grid: GridId) {
  const data = getRoot(state).gridSorting

  if (!data || !data[grid]) {
    return emptySortOrder
  }

  return data[grid]
}

export function getGridColumnWidths(state: StoreState) {
  return getRoot(state).gridColumnWidth
}

export const selectGridColumnWidths = createSelector(
  getGridColumnWidths,
  (state: StoreState, grid: GridId) => grid,
  (data, grid): ColumnWidths | undefined => {
    if (!data || !data[grid]) {
      return undefined
    }

    return data[grid]
  },
)

export function selectFixedQueueGridHeights(state: StoreState) {
  return getRoot(state).fixedQueueGridHeight
}
